import { useMutation } from "@tanstack/react-query";
import { useSelector, useDispatch } from "react-redux";

// MUI
import { Grid, Typography, Skeleton } from "@mui/material";

// Components
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// API
import { updateUser } from "@/User/api/UserPreferences/mutations";
// Redux
import { actions } from "@/redux/reducers/User";
// Hooks
import { useLocations } from "@/Common/hooks/Location/useLocations";
// Constants
import { tenant } from "@/Constants/globalConfig";
import { ILocation } from "@/Common/interfaces/location";
import { IUser } from "@/Common/interfaces/user";
import { RootState } from "@/redux/store";
import { errorLogger } from "@/Common/helpers/logger";

export default function LandingLocation() {
	const user = useSelector((state: RootState) => state.user.user);
	const { locations, isLoading } = useLocations(tenant);
	const dispatch = useDispatch();
	const landingLocation = locations?.find((item) => item.locationId === user?.preferences?.landingLocation);
	const { mutate, isPending } = useMutation({ mutationFn: (preferences: IUser) => updateUser(user?._id, user?.locationId, preferences) });
	const accessLocationPermissions = user?.actions?.filter((item: string) => /ACCESS_LOCATION/.exec(item));
	const LOCATION_ID_POSITION = 3;
	const accessLocationList = accessLocationPermissions?.map((item: string) => item.split("_", LOCATION_ID_POSITION).pop());
	const locationsList = locations?.filter((loc) => accessLocationList?.includes(loc.locationId));

	const changingLandingLocation = (value: ILocation) => {
		mutate(
			{
				preferences: {
					landingLocation: value?.locationId || "",
					landingPage: user?.preferences?.landingPage,
				},
			},
			{
				onSuccess: (res) => {
					if (!res) {
						return;
					}
					dispatch(
						actions.SET_USER({
							user: {
								...user,
								preferences: res.preferences,
							},
						})
					);
				},
				onError: (err) => errorLogger(err, { function: "changingLandingLocation", message: "Failed to change landing location." }),
			}
		);
	};

	return (
		<Grid id="1" item className="scrollable-content">
			<Typography className="user-preferences-section-text">Landing Location</Typography>
			{isPending ? (
				<Skeleton
					sx={{
						borderRadius: "4px",
					}}
					variant="rectangular"
					width={240}
					height={30}
				/>
			) : (
				<SmartSelector
					showBy="name"
					selectBy="locationId"
					selected={landingLocation}
					options={locationsList?.sort((item1: ILocation, item2: ILocation) => (item1.name > item2.name ? 1 : -1)) || []}
					inputProps={{
						placeholder: "Select Landing Location",
					}}
					clearable
					disabled={isLoading}
					loading={isLoading}
					sx={{ width: "240px" }}
					onChangeAction={(value) => changingLandingLocation(value as ILocation)}
				/>
			)}
		</Grid>
	);
}
